import React, { useCallback, useEffect, useState } from 'react';

import CoContributionsModal from '~support-at-home/components/co-contributions/CoContributionsModal';

import ClientInvoicesModal from '~direct-debits/components/payment-batches/ClientInvoicesModal';
import PaymentBatchDetail from '~direct-debits/components/payment-batches/PaymentBatchDetail';
import PaymentBatchesList from '~direct-debits/components/payment-batches/PaymentBatchesList';

import { useGetPaymentBatchDetailQuery } from '~direct-debits/actions/paymentBatches';

import { setTitle } from '~main/actions/login';

interface ComponentProps {
  isActive?: boolean;
}

const PaymentBatchesContainer: React.FC<ComponentProps> = ({ isActive = true }) => {
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  // Pull the cached batch detail to label the invoices modal with the client
  // name (rather than just the numeric ID). The query is already in cache as
  // soon as the level-2 view is opened, so this hook does not trigger a
  // second fetch — `skip` further protects against firing on the level-1 view.
  const { data: batchDetail } = useGetPaymentBatchDetailQuery(
    { batch_id: selectedBatchId ?? '' },
    { skip: selectedBatchId === null },
  );

  useEffect(() => {
    if (isActive) setTitle('Direct Debits — Payment Batches');
  }, [isActive]);

  const handleSelectBatch = useCallback((batchId: string) => {
    setSelectedBatchId(batchId);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedBatchId(null);
    setSelectedClientId(null);
    // Defensive: if the user reaches handleBackToList while a level-3 modal
    // is still open (e.g. via keyboard), the CoCon modal would otherwise
    // remain mounted/open on top of the level-1 list.
    setSelectedInvoiceId(null);
  }, []);

  const handleViewInvoices = useCallback((clientId: number) => {
    setSelectedClientId(clientId);
  }, []);

  const handleCloseInvoicesModal = useCallback(() => {
    setSelectedClientId(null);
    setSelectedInvoiceId(null);
  }, []);

  const handleViewInvoice = useCallback((invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
  }, []);

  // Reactstrap controlled modal helpers — the CoCon modal expects setIsOpen
  // rather than onClose, so adapt the callback shape here.
  const setInvoiceModalOpen = useCallback((open: boolean) => {
    if (!open) setSelectedInvoiceId(null);
  }, []);

  const selectedClientName = batchDetail?.clients.find(
    (client) => client.client_id === selectedClientId,
  )?.client_name;

  return (
    <div data-cy="dd-payment-batches-container">
      {selectedBatchId === null ? (
        <PaymentBatchesList isActive={isActive} onSelectBatch={handleSelectBatch} />
      ) : (
        <PaymentBatchDetail
          batchId={selectedBatchId}
          onBack={handleBackToList}
          onViewInvoices={handleViewInvoices}
        />
      )}
      <ClientInvoicesModal
        isOpen={selectedClientId !== null}
        onClose={handleCloseInvoicesModal}
        batchId={selectedBatchId}
        clientId={selectedClientId}
        clientName={selectedClientName}
        onViewInvoice={handleViewInvoice}
      />
      {/*
        Level-3 invoice details — the existing CoCon modal already accepts an
        invoice ID via its (misleadingly named) `clientId` prop and handles its
        own data load + status transitions. Reusing it keeps the experience
        consistent with the SaH co-contributions screen and avoids duplicating
        the invoice editor here. TAV-2053 is the consolidation candidate to
        rename that prop and decouple the modal from the SaH module.
      */}
      <CoContributionsModal
        isOpen={selectedInvoiceId !== null}
        setIsOpen={setInvoiceModalOpen}
        clientId={selectedInvoiceId}
      />
    </div>
  );
};

export default PaymentBatchesContainer;
